import { Injectable } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';

export interface NetworkErrorMessage {
  title: string;
  message: string;
  type: 'no_internet' | 'api_down';
}

/**
 * Classifies HTTP/network errors and returns user-friendly messages
 * for "no internet" vs "API/server not running" so the app can display the right message.
 */
@Injectable({ providedIn: 'root' })
export class NetworkErrorService {
  /**
   * Returns a user-friendly title and message for connection/API errors, or null if not applicable.
   * - No internet: when browser reports offline or request failed due to network.
   * - API not running: when server is unreachable (connection refused), or 502/503/504.
   */
  getUserMessage(error: HttpErrorResponse | unknown): NetworkErrorMessage | null {
    if (!error || typeof (error as any)?.status !== 'number') {
      return null;
    }
    const err = error as HttpErrorResponse;
    const status = err.status;

    // Server-side errors: gateway or server unavailable
    if (status === 502 || status === 503 || status === 504) {
      return {
        type: 'api_down',
        title: 'Server Unavailable',
        message: 'The server is temporarily unavailable. Please try again later.'
      };
    }

    // status 0 = request never reached server (no network, connection refused, CORS, etc.)
    if (status === 0) {
      const hasNetwork = typeof navigator !== 'undefined' && navigator.onLine;
      if (!hasNetwork) {
        return {
          type: 'no_internet',
          title: 'No Internet Connection',
          message: 'Please check your internet connection and try again.'
        };
      }
      return {
        type: 'api_down',
        title: 'Cannot Reach Server',
        message: 'Unable to connect to the server. Please check if the service is running and try again.'
      };
    }

    return null;
  }
}
